import React, {useCallback, useEffect, useRef, useState} from 'react';
import {useDropzone} from 'react-dropzone';
import AvatarEditor from 'react-avatar-editor';
import _debounce from 'lodash/debounce';
import _isEqual from 'lodash/isEqual';
import '../../assets/styles/components/imageeditor.css';
import PropTypes from 'prop-types';
import {toast} from 'react-toastify';
import {useTranslation} from 'react-i18next';
import {Trans} from 'react-i18next';

const tryRemoveUrlParameter = img => {
  const isUrl = typeof img === 'string' && img.startsWith('http');
  const idx = isUrl ? img.indexOf('?p=') : -1;
  return idx < 0 ? img : img.substring(0, idx);
};

export default function ImageEditor({
  imageDataUrl = undefined,
  imageUrl = undefined,
  enableScale = false,
  defaultScale = 1,
  onDataUrlUpdate = () => {},
  onImageClear = () => {},
  imageWidth = 300,
  imageHeight = 300,
  displayWidth = 300,
  displayHeight = 300,
  setProductImage,
  defaultPosition = {x: 0.5, y: 0.5},
  imageFormat = 'png',
  imageFile = undefined,
  onImageLoaded = () => {},
}) {
  const {t} = useTranslation();
  const [image, setImage] = useState('');
  const [scale, setScale] = useState(defaultScale);
  const [position, setPosition] = useState(defaultPosition);
  const [dragging, setDragging] = useState(false);
  const editorRef = useRef(null);
  const maxWidth = displayWidth ? displayWidth : imageWidth;
  const maxHeight = displayHeight ? displayHeight : imageHeight;
  useEffect(() => {
    if (imageDataUrl) {
      setImage(imageDataUrl);
    } else {
      const isUrl =
        typeof imageUrl === 'string' &&
        (imageUrl.startsWith('http') || imageUrl.startsWith('//'));
      const reqImgUrl = isUrl
        ? `${imageUrl}?p=${new Date().getTime()}`
        : imageUrl;
      imageFile ? setImage(imageFile) : setImage(reqImgUrl);
    }
  }, [imageUrl, imageDataUrl, imageFile]);

  const updateImage = () => {
    const ref = editorRef.current;
    if (ref) {
      const canvas = ref.getImageScaledToCanvas();
      const format = imageFormat === 'jpg' ? 'image/jpeg' : 'image/png';
      const dataURL = canvas.toDataURL(format);
      onDataUrlUpdate(
        dataURL,
        ref.props.scale,
        ref.props.position,
        ref.getCroppingRect()
      );
      setProductImage && setProductImage(dataURL);
    }
  };

  const handleImageChange = useCallback(
    _debounce(updateImage, 200, {
      leading: false,
    }),
    []
  );

  const handleLoadSuccess = () => {
    const isUrl = typeof image === 'string' && image.startsWith('http');
    const imgUrl = tryRemoveUrlParameter(image);
    const shouldUpdate = !_isEqual(imgUrl, imageUrl);
    if (isUrl && shouldUpdate) {
      setScale(defaultScale);
      setPosition(defaultPosition);
      updateImage();
    } else if (!isUrl && image) {
      setScale(defaultScale);
      setPosition(defaultPosition);
      // updateImage();
    }
  };

  const handlePositionChange = pos => {
    setPosition(pos);
  };

  const handleChangeScale = e => {
    const scale = e.target.value;
    setScale(Number(scale));
  };

  const handleImageClear = e => {
    e.stopPropagation();
    setImage('');
    onImageClear();
  };

  const handleMouseMove = e => {
    setDragging(true);
  };

  const handleDropImage = useCallback(
    files => {
      const file = files[0];
      if (!file || !file.type.includes('image')) {
        toast.error(
          t(
            'c.i.image.editor.invalid.file',
            'Invalid file format. Please choose an image file.'
          )
        );
        return;
      }
      setImage(file);
      onImageLoaded(file);
    },
    [t]
  );

  const {getRootProps, getInputProps} = useDropzone({
    accept: 'image/*',
    multiple: false,
    onDrop: handleDropImage,
  });

  return (
    <div className="image-editor-block">
      <div
        {...getRootProps({
          className: 'image-editor-dropzone-block',
          style: {
            maxWidth: '100%',
            width: maxWidth,
            height: maxHeight,
            aspectRatio: (imageWidth / imageHeight).toString(),
          },
          onClick: e => {
            if (dragging) {
              e.stopPropagation();
              setDragging(false);
            }
          },
        })}
      >
        <input {...getInputProps()} />
        {!!image && (
          <div>
            <AvatarEditor
              className="canvas-editor"
              ref={editorRef}
              onLoadSuccess={handleLoadSuccess}
              onImageChange={handleImageChange}
              onMouseMove={handleMouseMove}
              onPositionChange={handlePositionChange}
              image={image}
              color={[230, 230, 230, 0.6]}
              style={{
                height: imageHeight + 'px',
                width: imageWidth + 'px',
                maxHeight: maxHeight + 'px',
                maxWidth: maxWidth + 'px',
              }}
              scale={scale}
              position={position}
              border={3}
              width={imageWidth}
              height={imageHeight}
              crossOrigin="anonymous"
            />
            <button
              type="button"
              className="image-editor-close btn btn-outline-secondary btn-icon btn-sm"
              onClick={handleImageClear}
            >
              <i className="la la-remove" />
            </button>
          </div>
        )}
        {!image && (
          <table className="image-editor-hint">
            <tbody>
              <tr>
                <td style={{verticalAlign: 'middle'}}>
                  <h5>
                    <Trans i18nKey="c.i.imageeditor.guide.text">
                      Drop an image here or click to upload
                    </Trans>
                  </h5>
                  ({imageWidth}W x {imageHeight}H)
                </td>
              </tr>
            </tbody>
          </table>
        )}
      </div>
      {enableScale && (
        <div className="image-editor-image-scale-block">
          <i className="fa fa-image" />
          <input
            className="image-editor-image-scale"
            type="range"
            step="0.01"
            min="0.1"
            max="2"
            value={scale}
            onChange={handleChangeScale}
            style={{
              minWidth: '40px',
              maxWidth: '130px',
              width: `${maxWidth / 3}px`,
            }}
          />
          <i className="fa fa-image fa-2x" />
        </div>
      )}
    </div>
  );
}

ImageEditor.propTypes = {
  imageDataUrl: PropTypes.string,
  imageUrl: PropTypes.string,
  enableScale: PropTypes.bool,
  defaultScale: PropTypes.number,
  onDataUrlUpdate: PropTypes.func,
  onImageClear: PropTypes.func,
  imageWidth: PropTypes.number,
  imageHeight: PropTypes.number,
  maxWidth: PropTypes.number,
  maxHeight: PropTypes.number,
};
